import Phaser from "phaser";
import type { TutorialTranslate } from "./ports.js";
import {
  computeTutorialSkipButtonFrame,
  type TutorialSafeAreaInsets,
} from "./TutorialSkipButtonLayout.js";

export interface TutorialSkipButtonOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly textColor: string;
  readonly textResolution: number;
  readonly translate: TutorialTranslate;
  readonly labelKey: string;
  readonly safeAreaInsets?: TutorialSafeAreaInsets;
  readonly messagePanel?: import("./TutorialSkipButtonLayout.js").TutorialSkipButtonAnchorRect | null;
  readonly onSkip: () => void;
}

const SKIP_BUTTON = {
  bgColor: 0x111827,
  bgAlpha: 0.86,
  strokeColor: 0xffffff,
  strokeAlpha: 0.22,
} as const;

export function drawTutorialSkipButton(
  container: Phaser.GameObjects.Container,
  options: TutorialSkipButtonOptions,
): void {
  const labelText = options.translate(options.labelKey);
  const label = container.scene.add
    .text(0, 0, labelText, {
      fontFamily: options.fontFamily,
      fontSize: `${Math.max(12, options.fontSize - 2)}px`,
      color: options.textColor,
      align: "center",
    })
    .setOrigin(0.5)
    .setResolution(options.textResolution);
  const frame = computeTutorialSkipButtonFrame({
    screenWidth: options.screenWidth,
    screenHeight: options.screenHeight,
    labelWidth: label.width,
    safeAreaInsets: options.safeAreaInsets,
    messagePanel: options.messagePanel,
  });
  const bg = container.scene.add
    .rectangle(
      frame.x,
      frame.y,
      frame.width,
      frame.height,
      SKIP_BUTTON.bgColor,
      SKIP_BUTTON.bgAlpha,
    )
    .setOrigin(0.5)
    .setStrokeStyle(1, SKIP_BUTTON.strokeColor, SKIP_BUTTON.strokeAlpha)
    .setInteractive({ useHandCursor: true });
  label.setPosition(frame.x, frame.y);

  const handleSkip = (
    _pointer: Phaser.Input.Pointer,
    _localX: number,
    _localY: number,
    event?: Phaser.Types.Input.EventData,
  ): void => {
    event?.stopPropagation();
    options.onSkip();
  };
  bg.on("pointerup", handleSkip);
  label.setInteractive({ useHandCursor: true }).on("pointerup", handleSkip);
  container.add(bg);
  container.add(label);
}
